import { useState, useRef, useCallback } from "react"
import { AcademicianButton } from "@/components/academician/academician-button"
import { PlusIcon, SearchIcon, BookOpenIcon, VideoIcon, FileQuestionIcon, PencilIcon, EyeIcon, TrashIcon, MoreHorizontalIcon, GripVerticalIcon } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

export interface LearningModule {
  id: string
  title: string
  description: string
  status: "published" | "draft"
  position: number
  recommendedWeek: number | null
  chaptersCount: number
  videosCount: number
  quizzesCount: number
  cohorts: string[]
}

interface ManageModulesPageProps {
  modules?: LearningModule[]
  className?: string
}

const defaultModules: LearningModule[] = [
  {
    id: "1",
    title: "Customer Discovery Fundamentals",
    description: "Learn how to identify and validate your target customers.",
    status: "published",
    position: 0,
    recommendedWeek: 1,
    chaptersCount: 3,
    videosCount: 5,
    quizzesCount: 1,
    cohorts: ["Startup Bootcamp 2026"],
  },
  {
    id: "2",
    title: "Problem Validation",
    description: "Master techniques for validating problems before building solutions.",
    status: "published",
    position: 1,
    recommendedWeek: 2,
    chaptersCount: 2,
    videosCount: 4,
    quizzesCount: 1,
    cohorts: ["Startup Bootcamp 2026"],
  },
  {
    id: "3",
    title: "Solution Design",
    description: "Design solutions that address validated customer problems.",
    status: "draft",
    position: 2,
    recommendedWeek: 3,
    chaptersCount: 1,
    videosCount: 2,
    quizzesCount: 0,
    cohorts: [],
  },
]

type FilterStatus = "all" | "published" | "draft"

export function ManageModulesPage({
  modules: initialModules = defaultModules,
  className,
}: ManageModulesPageProps) {
  const [modules, setModules] = useState(initialModules)
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const dragCounter = useRef(0)

  const canDrag = filterStatus === "all" && searchQuery === ""

  const persistOrder = useCallback((reorderedModules: LearningModule[]) => {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    fetch("/admin/learning_modules/reorder", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken || "",
      },
      body: JSON.stringify({ module_ids: reorderedModules.map((m) => m.id) }),
    })
  }, [])

  const handleDragStart = (e: React.DragEvent, modId: string) => {
    setDraggedId(modId)
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", modId)
    const row = (e.target as HTMLElement).closest("tr")
    if (row) e.dataTransfer.setDragImage(row, 20, 20)
  }

  const handleDragEnter = (modId: string) => {
    dragCounter.current++
    setDragOverId(modId)
  }

  const handleDragLeave = () => {
    dragCounter.current--
    if (dragCounter.current === 0) setDragOverId(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    dragCounter.current = 0
    setDragOverId(null)
    setDraggedId(null)
    if (!draggedId || draggedId === targetId) return

    const fromIndex = modules.findIndex((m) => m.id === draggedId)
    const toIndex = modules.findIndex((m) => m.id === targetId)
    if (fromIndex === -1 || toIndex === -1) return

    const reordered = [...modules]
    const [moved] = reordered.splice(fromIndex, 1)
    reordered.splice(toIndex, 0, moved)
    const updated = reordered.map((m, i) => ({ ...m, position: i }))
    setModules(updated)
    persistOrder(updated)
  }

  const handleDragEnd = () => {
    dragCounter.current = 0
    setDraggedId(null)
    setDragOverId(null)
  }

  const filteredModules = modules.filter((mod) => {
    const matchesStatus = filterStatus === "all" || mod.status === filterStatus
    const matchesSearch = mod.title.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesStatus && matchesSearch
  })

  const stats = {
    total: modules.length,
    published: modules.filter((m) => m.status === "published").length,
    draft: modules.filter((m) => m.status === "draft").length,
    totalChapters: modules.reduce((sum, m) => sum + m.chaptersCount, 0),
    totalVideos: modules.reduce((sum, m) => sum + m.videosCount, 0),
  }

  const handleCreateModule = () => {
    window.location.href = "/admin/learning_modules/new"
  }

  const handleViewModule = (mod: LearningModule) => {
    window.location.href = `/admin/learning_modules/${mod.id}`
  }

  const handleEditModule = (mod: LearningModule) => {
    window.location.href = `/admin/learning_modules/${mod.id}/edit`
  }

  const handleDeleteModule = (mod: LearningModule) => {
    if (mod.cohorts.length > 0) {
      alert(`Cannot delete "${mod.title}" — it is used in ${mod.cohorts.length} cohort(s).`)
      return
    }
    if (confirm(`Are you sure you want to delete "${mod.title}"?`)) {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
      fetch(`/admin/learning_modules/${mod.id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrfToken || "",
          "Accept": "text/html",
        },
      }).then(() => {
        window.location.reload()
      })
    }
  }

  const filterButtons: Array<{ label: string; value: FilterStatus; count: number }> = [
    { label: "All", value: "all", count: stats.total },
    { label: "Published", value: "published", count: stats.published },
    { label: "Draft", value: "draft", count: stats.draft },
  ]

  return (
    <div className={cn("min-h-screen bg-background", className)}>
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Learning Modules
              </h1>
              <p className="text-muted-foreground mt-1">
                Create and manage your course modules
              </p>
            </div>
            <AcademicianButton
              variant="primary"
              leftIcon={<PlusIcon className="h-4 w-4" />}
              onClick={handleCreateModule}
            >
              Create Module
            </AcademicianButton>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="text-sm text-muted-foreground mb-1">Total Modules</div>
            <div className="text-3xl font-bold text-foreground">{stats.total}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="text-sm text-muted-foreground mb-1">Published</div>
            <div className="text-3xl font-bold text-foreground">{stats.published}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="text-sm text-muted-foreground mb-1">Total Chapters</div>
            <div className="text-3xl font-bold text-foreground">{stats.totalChapters}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="text-sm text-muted-foreground mb-1">Total Videos</div>
            <div className="text-3xl font-bold text-foreground">{stats.totalVideos}</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex flex-wrap gap-2">
              {filterButtons.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setFilterStatus(filter.value)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    filterStatus === filter.value
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>
            <div className="relative flex-1 lg:max-w-xs ml-auto">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search modules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Modules Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {filteredModules.length === 0 ? (
            <div className="p-12 text-center">
              <div className="bg-muted rounded-full p-6 mb-4 inline-flex">
                <BookOpenIcon className="h-12 w-12 text-muted-foreground" />
              </div>
              <div className="text-foreground font-semibold text-lg mb-2">
                {searchQuery ? "No modules found" : "No modules yet"}
              </div>
              <div className="text-muted-foreground mb-4">
                {searchQuery
                  ? "Try adjusting your search or filters."
                  : "Get started by creating your first learning module."}
              </div>
              {!searchQuery && (
                <AcademicianButton
                  variant="primary"
                  onClick={handleCreateModule}
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                >
                  Create Your First Module
                </AcademicianButton>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    {canDrag && (
                      <th className="w-10 px-2 py-3"></th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Module
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Recommended Week
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Content
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Used In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card">
                  {filteredModules.map((mod) => (
                    <tr
                      key={mod.id}
                      className={cn(
                        "border-b border-border hover:bg-muted/50 transition-colors",
                        draggedId === mod.id && "opacity-40",
                        dragOverId === mod.id && draggedId !== mod.id && "border-t-2 border-t-primary"
                      )}
                      draggable={canDrag}
                      onDragStart={(e) => handleDragStart(e, mod.id)}
                      onDragEnter={() => handleDragEnter(mod.id)}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, mod.id)}
                      onDragEnd={handleDragEnd}
                    >
                      {canDrag && (
                        <td className="w-10 px-2 py-4 cursor-grab active:cursor-grabbing">
                          <GripVerticalIcon className="h-4 w-4 text-muted-foreground" />
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <div className="font-medium text-foreground">{mod.title}</div>
                        <div className="text-sm text-muted-foreground line-clamp-1">{mod.description}</div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          className={cn(
                            mod.status === "published"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                          )}
                        >
                          {mod.status === "published" ? "Published" : "Draft"}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {mod.recommendedWeek ? `Week ${mod.recommendedWeek}` : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1" title="Chapters">
                            <BookOpenIcon className="h-4 w-4" />
                            {mod.chaptersCount}
                          </span>
                          <span className="flex items-center gap-1" title="Videos">
                            <VideoIcon className="h-4 w-4" />
                            {mod.videosCount}
                          </span>
                          {mod.quizzesCount > 0 && (
                            <span className="flex items-center gap-1" title="Quizzes">
                              <FileQuestionIcon className="h-4 w-4" />
                              {mod.quizzesCount}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {mod.cohorts.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {mod.cohorts.map((cohort) => (
                              <Badge key={cohort} variant="outline" className="text-xs">
                                {cohort}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Not assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontalIcon className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleViewModule(mod)}
                              className="cursor-pointer"
                            >
                              <EyeIcon className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleEditModule(mod)}
                              className="cursor-pointer"
                            >
                              <PencilIcon className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteModule(mod)}
                              className="cursor-pointer text-destructive"
                              disabled={mod.cohorts.length > 0}
                            >
                              <TrashIcon className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {filteredModules.length > 0 && (
          <div className="mt-4 text-sm text-muted-foreground text-center">
            Showing {filteredModules.length} of {modules.length} modules
          </div>
        )}
      </div>
    </div>
  )
}
